import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, fmtDate, API } from "../lib/api";
import { ChevronLeft, ChevronRight, Download, FileSpreadsheet, ChevronLeft as Prev } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { RECEPTIONIST: "Receptionist", HOUSEKEEPER: "Housekeeper", SECURITY: "Security Guard", OTHER: "Other" };

function ym(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }

export default function AttendanceMonthlyPage() {
    const { siteId } = useParams();
    const [site, setSite] = useState(null);
    const [month, setMonth] = useState(ym(new Date()));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, m] = await Promise.all([
                api.get(`/sites/${siteId}`),
                api.get(`/sites/${siteId}/attendance/month`, { params: { month } }),
            ]);
            setSite(s.data);
            setData(m.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [siteId, month]);

    useEffect(() => { load(); }, [load]);

    function shift(delta) {
        const [y, m] = month.split("-").map(Number);
        const d = new Date(y, m - 1 + delta, 1);
        setMonth(ym(d));
    }

    async function download(kind) {
        try {
            const token = localStorage.getItem("fx_token");
            const url = `${API}/sites/${siteId}/attendance/month/export.${kind}?month=${month}`;
            const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `attendance-${site?.name || siteId}-${month}.${kind}`;
            a.click();
            URL.revokeObjectURL(a.href);
        } catch (e) { toast.error(`Download failed: ${e.message}`); }
    }

    const monthLabel = useMemo(() => {
        const [y, m] = month.split("-");
        return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
    }, [month]);

    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to={`/sites/${siteId}/attendance`} className="text-xs text-gray-500 hover:text-[#1E3355] inline-flex items-center gap-1 mb-3" data-testid="monthly-back">
                <Prev className="w-3 h-3" /> Back to daily attendance
            </Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Monthly Attendance</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">{site?.name || "…"}</h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md p-1">
                        <button onClick={() => shift(-1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="monthly-prev"><ChevronLeft className="w-4 h-4" /></button>
                        <div className="px-3 text-sm font-medium text-[#1E3355] min-w-[140px] text-center">{monthLabel}</div>
                        <button onClick={() => shift(1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="monthly-next"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                    <button onClick={() => download("csv")} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-white border border-gray-200 text-gray-700 hover:text-[#1E3355]" data-testid="monthly-export-csv">
                        <FileSpreadsheet className="w-3.5 h-3.5" /> Export CSV
                    </button>
                    <button onClick={() => download("pdf")} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-[#1E3355] hover:bg-[#15243C] text-white" data-testid="monthly-export-pdf">
                        <Download className="w-3.5 h-3.5" /> Export PDF
                    </button>
                </div>
            </div>

            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-xs sm:text-sm" data-testid="monthly-attendance-table">
                        <thead className="bg-[#F4F6F9]">
                            <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                                <th className="px-3 py-2 sticky left-0 bg-[#F4F6F9]">Staff</th>
                                <th className="px-2 py-2">Role</th>
                                {data?.days.map((d) => (
                                    <th key={d} className="px-1 py-2 text-center text-gray-400 font-medium tabular-nums">{d.slice(-2)}</th>
                                ))}
                                <th className="px-2 py-2 text-center">P</th>
                                <th className="px-2 py-2 text-center">A</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? <tr><td colSpan={30} className="p-6 text-center text-sm text-gray-500">Loading…</td></tr> :
                             data?.rows.length === 0 ? <tr><td colSpan={30} className="p-6 text-center text-sm text-gray-500" data-testid="monthly-empty">No staff / attendance records.</td></tr> :
                             data?.rows.map((r) => (
                                <tr key={r.staff.id} data-testid={`monthly-row-${r.staff.name}`}>
                                    <td className="px-3 py-2 font-medium text-[#1E3355] sticky left-0 bg-white whitespace-nowrap">{r.staff.name}</td>
                                    <td className="px-2 py-2 text-xs text-gray-500">{ROLE_LABEL[r.staff.role]}</td>
                                    {r.cells.map((c) => (
                                        <td key={c.date} className="px-1 py-2 text-center" title={c.absence_reason || ""} data-testid={`cell-${r.staff.name}-${c.date}`}>
                                            {c.status === "PRESENT" && <span className="inline-block w-5 h-5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold leading-5">P</span>}
                                            {c.status === "ABSENT" && <span className="inline-block w-5 h-5 rounded-full bg-red-100 text-[#E02B2B] text-[10px] font-bold leading-5">A</span>}
                                            {!c.status && <span className="text-gray-300">–</span>}
                                        </td>
                                    ))}
                                    <td className="px-2 py-2 text-center font-semibold text-emerald-700">{r.present}</td>
                                    <td className="px-2 py-2 text-center font-semibold text-[#E02B2B]">{r.absent}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
